import { Tooltip } from 'bootstrap';
import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlobalContext } from '../../../library/Core/GlobalContext';
import { getDomainValueForCode } from '../../../library/Core/SmartFunctions';
import { InstituteRow } from './BurnerAnalysisService';
import './burner-analysis.css';
import { clientBadgeClass, coverageColor, formatDate, relativeAgo } from './utils';

const HeaderInfo = ({ text }: { text: string }) => (
    <i
        className="bi bi-info-circle ms-1"
        style={{ fontSize: 11, color: '#64748b', cursor: 'help' }}
        data-bs-toggle="tooltip"
        data-bs-placement="top"
        data-bs-html="true"
        data-bs-title={text}
        data-bs-custom-class="burner-analysis-tooltip"
    />
);

interface Props {
    rows: InstituteRow[];
    summary: { instituteCount: number; totalAlumni: number; burnersDone: number };
    onRunInstitute: (institute: InstituteRow) => void;
}

const PAGE_INITIAL = 20;
const PAGE_INCREMENT = 10;

const Grid = ({ rows, summary, onRunInstitute }: Props) => {
    const navigate = useNavigate();
    const globalCtx = useContext(GlobalContext);
    const stateLike: any = { domain: globalCtx?.globalState?.domain ?? new Map() };

    const [visibleCount, setVisibleCount] = useState(PAGE_INITIAL);
    const loadMoreRef = useRef<HTMLTableRowElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        setVisibleCount(PAGE_INITIAL);
    }, [rows]);

    useEffect(() => {
        if (!containerRef.current) return;
        const els = containerRef.current.querySelectorAll('[data-bs-toggle="tooltip"]');
        const tips = Array.from(els).map((el) => new Tooltip(el));
        return () => tips.forEach((t) => t.dispose());
    }, [rows, visibleCount]);

    useEffect(() => {
        const sentinel = loadMoreRef.current;
        if (!sentinel) return;
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                setVisibleCount((prev) => (prev >= rows.length ? prev : prev + PAGE_INCREMENT));
            }
        });
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [rows, visibleCount]);

    const visibleRows = rows.slice(0, visibleCount);
    const hasMore = visibleCount < rows.length;

    const renderRunButton = (row: InstituteRow) => {
        if (Number(row.totalAlumni) === 0) {
            return <span className="font-12 text-muted">—</span>;
        }
        if (!row.isRunAllowed && row.cooldownDaysRemaining > 0) {
            return (
                <button
                    type="button"
                    className="btn btn-sm"
                    disabled
                    title={`Available in ${row.cooldownDaysRemaining}d (cooldown: 6 months)`}
                    style={{
                        background: '#f8fafc',
                        border: '0.5px solid #cbd5e1',
                        color: '#94a3b8',
                        cursor: 'not-allowed',
                    }}>
                    In {row.cooldownDaysRemaining}d
                </button>
            );
        }
        return (
            <button
                type="button"
                className="btn btn-sm"
                onClick={() => onRunInstitute(row)}
                title={`Last run: ${row.lastBurnerRun ? formatDate(row.lastBurnerRun) : 'Never'}`}
                style={{
                    background: '#e6f1fb',
                    border: '0.5px solid #185fa5',
                    color: '#0c447c',
                    fontWeight: 500,
                }}>
                ▶ Run analysis
            </button>
        );
    };

    return (
        <div
            ref={containerRef}
            className="white-block white-block-notopborderradius p-0 mb-3"
            style={{ overflow: 'hidden' }}>
            <div className="d-flex justify-content-between align-items-center px-4 py-2 border-bottom">
                <span className="font-13 font-500">
                    {summary.instituteCount} institutes &nbsp;·&nbsp;
                    {Number(summary.totalAlumni).toLocaleString()} total alumni &nbsp;·&nbsp;
                    {Number(summary.burnersDone).toLocaleString()} burners done
                </span>
                <span className="font-11 text-muted">
                    Cooldown: 6 months per institute &nbsp;·&nbsp; Click institute name to view alumni profiles
                </span>
            </div>
            <div className="table-responsive">
                <table className="table mb-0" style={{ minWidth: 1100 }}>
                    <thead style={{ background: '#e6f1fb' }}>
                        <tr>
                            <th>Institute name</th>
                            <th>City</th>
                            {/* <th>Group</th> */}
                            <th>Type</th>
                            <th>Client</th>
                            <th className="text-center">Total alumni</th>
                            <th className="text-center">
                                Burners done
                                <HeaderInfo text="Count of alumni profiles in this institute that have at least one burner-analysis row.<br/>Shown alongside the % of total alumni this represents." />
                            </th>
                            <th className="text-center">
                                % Active w/ burner
                                <HeaderInfo text="Of all alumni who have a burner-analysis row in this institute, the % whose status is one of:<br/>ACTIVE, PROFILE_IN_PROGRESS, PENDING_EMAIL_VERIFICATION.<br/><br/><b>Formula:</b> active alumni with burner ÷ alumni with burner × 100<br/><br/>Colour-coded: green ≥ 75%, amber 50–74%, red &lt; 50%." />
                            </th>
                            <th className="text-center">
                                % Raw / semi / dec w/ burner
                                <HeaderInfo text="Of all alumni who have a burner-analysis row in this institute, the % whose status is one of:<br/>RAW_PROFILE, SEMI_VERIFIED_USER.<br/><br/><b>Formula:</b> non-active alumni with burner ÷ alumni with burner × 100" />
                            </th>
                            <th className="text-center">Last burner run</th>
                            <th className="text-center">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length > 1 && (
                            <tr style={{ background: '#f8fafc', fontWeight: 500 }}>
                                <td>All institutes</td>
                                <td className="text-muted">—</td>
                                {/* <td className="text-muted">—</td> */}
                                <td className="text-muted">—</td>
                                <td className="text-muted">—</td>
                                <td className="text-center">{Number(summary.totalAlumni).toLocaleString()}</td>
                                <td className="text-center">{Number(summary.burnersDone).toLocaleString()}</td>
                                <td className="text-center text-muted">—</td>
                                <td className="text-center text-muted">—</td>
                                <td className="text-center text-muted">—</td>
                                <td className="text-center text-muted">—</td>
                            </tr>
                        )}
                        {visibleRows.map((row) => {
                            const donePct =
                                Number(row.totalAlumni) > 0
                                    ? Math.round((Number(row.burnersDone) / Number(row.totalAlumni)) * 100)
                                    : 0;
                            const cityLabel = row.cityCode
                                ? getDomainValueForCode(row.cityCode, 'CITY_CODE', stateLike) || row.cityCode
                                : '—';
                            // const groupResolved = row.instituteGroupId
                            //     ? getDomainValueForCode(row.instituteGroupId, 'SCHOOL_GROUP', stateLike)
                            //     : '';
                            // const groupLabel =
                            //     groupResolved && String(groupResolved) !== String(row.instituteGroupId)
                            //         ? groupResolved
                            //         : '—';
                            const typeLabel = row.instituteTypeCode
                                ? getDomainValueForCode(row.instituteTypeCode, 'INSTITUTE_TYPE_CODE', stateLike) ||
                                  String(row.instituteTypeCode).replace(/_/g, ' ')
                                : '—';
                            const clientLabel = row.clientType
                                ? getDomainValueForCode(row.clientType, 'SCHOOL_CLIENT_STATUS_CODE', stateLike) ||
                                  row.clientType
                                : '—';
                            return (
                                <tr key={row.instituteId}>
                                    <td>
                                        <a
                                            role="button"
                                            className="tablink font-14 font-500"
                                            onClick={() =>
                                                navigate(`/support/burner-analysis/institute/${row.instituteId}`)
                                            }>
                                            {row.instituteName}
                                        </a>
                                    </td>
                                    <td className="font-12 text-muted">{cityLabel}</td>
                                    {/* <td className="font-12 text-muted">{groupLabel}</td> */}
                                    <td className="font-12 text-muted">{typeLabel}</td>
                                    <td>
                                        <span className={clientBadgeClass(row.clientType)}>{clientLabel}</span>
                                    </td>
                                    <td className="text-center">{Number(row.totalAlumni).toLocaleString()}</td>
                                    <td className="text-center">
                                        <div className="font-500">{Number(row.burnersDone).toLocaleString()}</div>
                                        <div className="font-11 text-muted">{donePct}%</div>
                                    </td>
                                    <td className="text-center">
                                        <span
                                            className="font-500"
                                            style={{ color: coverageColor(Number(row.activeWithBurnerPct)) }}>
                                            {Number(row.activeWithBurnerPct)}%
                                        </span>
                                    </td>
                                    <td className="text-center text-muted font-500">
                                        {Number(row.nonActiveWithBurnerPct)}%
                                    </td>
                                    <td className="text-center">
                                        {row.lastBurnerRun ? (
                                            <>
                                                <div className="font-12">{formatDate(row.lastBurnerRun)}</div>
                                                <div className="font-11 text-muted">{relativeAgo(row.lastBurnerRun)}</div>
                                            </>
                                        ) : (
                                            <span className="font-12 text-muted">Never run</span>
                                        )}
                                    </td>
                                    <td className="text-center">{renderRunButton(row)}</td>
                                </tr>
                            );
                        })}
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={11} className="text-center text-muted py-4">
                                    No institutes match the current filters.
                                </td>
                            </tr>
                        )}
                        {hasMore && (
                            <tr ref={loadMoreRef}>
                                <td colSpan={11} className="text-center text-muted font-12 py-2">
                                    Loading more institutes…
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default Grid;
